import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/Button';

interface PaginationProps {
  readonly page: number;
  readonly totalPages: number;
  readonly total: number;
  readonly limit: number;
  readonly onPageChange: (page: number) => void;
  readonly className?: string;
}

function rangeForPage(page: number, totalPages: number): readonly number[] {
  const window = 2;
  const start = Math.max(1, page - window);
  const end = Math.min(totalPages, page + window);
  const pages: number[] = [];
  for (let i = start; i <= end; i += 1) {
    pages.push(i);
  }
  return pages;
}

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  className,
}: PaginationProps): ReactElement {
  const safeTotalPages = Math.max(totalPages, 1);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pages = rangeForPage(page, safeTotalPages);
  const showFirst = pages.length > 0 && pages[0] !== 1;
  const showLast = pages.length > 0 && pages[pages.length - 1] !== safeTotalPages;

  return (
    <div
      className={cn(
        'flex flex-col-reverse items-center justify-between gap-3 sm:flex-row',
        className,
      )}
    >
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {total === 0
          ? 'Sin resultados'
          : `Mostrando ${String(from)}–${String(to)} de ${String(total)}`}
      </p>

      <nav aria-label="Paginación" className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Página anterior"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1);
          }}
        >
          <ChevronLeft />
        </Button>

        {showFirst ? (
          <>
            <PageButton
              page={1}
              currentPage={page}
              onClick={() => {
                onPageChange(1);
              }}
            />
            {pages[0] !== undefined && pages[0] > 2 ? (
              <span className="px-1 text-xs text-muted-foreground">…</span>
            ) : null}
          </>
        ) : null}

        {pages.map((p) => (
          <PageButton
            key={p}
            page={p}
            currentPage={page}
            onClick={() => {
              onPageChange(p);
            }}
          />
        ))}

        {showLast ? (
          <>
            {pages[pages.length - 1] !== undefined &&
            (pages[pages.length - 1] ?? 0) < safeTotalPages - 1 ? (
              <span className="px-1 text-xs text-muted-foreground">…</span>
            ) : null}
            <PageButton
              page={safeTotalPages}
              currentPage={page}
              onClick={() => {
                onPageChange(safeTotalPages);
              }}
            />
          </>
        ) : null}

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Página siguiente"
          disabled={page >= safeTotalPages}
          onClick={() => {
            onPageChange(page + 1);
          }}
        >
          <ChevronRight />
        </Button>
      </nav>
    </div>
  );
}

interface PageButtonProps {
  readonly page: number;
  readonly currentPage: number;
  readonly onClick: () => void;
}

function PageButton({ page, currentPage, onClick }: PageButtonProps): ReactElement {
  const isActive = page === currentPage;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-medium',
        'transition-colors duration-150',
        isActive
          ? 'bg-primary text-primary-foreground shadow-soft-1'
          : 'text-foreground hover:bg-muted',
      )}
    >
      {page}
    </button>
  );
}
